import { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';

interface ProductStatsProps {
  campaignId: string;
  onClose: () => void;
}

interface ProductStats {
  productId: string;
  productName: string;
  views: number;
  clicks: number;
  sales: number;
  revenue: number;
}

export default function ProductStats({ campaignId, onClose }: ProductStatsProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [products, setProducts] = useState<ProductStats[]>([]);
  const [dateRange, setDateRange] = useState({
    startDate: format(new Date().setDate(new Date().getDate() - 30), 'yyyy-MM-dd'),
    endDate: format(new Date(), 'yyyy-MM-dd')
  });

  useEffect(() => {
    fetchProductStats();
  }, [campaignId, dateRange]);

  const fetchProductStats = async () => {
    try {
      setLoading(true);
      setError('');

      // Buscar a campanha para obter a lista de produtos
      const campaignRef = doc(db, 'campaigns', campaignId);
      const campaignSnap = await getDoc(campaignRef);
      
      if (!campaignSnap.exists()) {
        throw new Error('Campanha não encontrada');
      }

      const campaign = campaignSnap.data();
      const productIds = campaign.products || [];

      // Buscar detalhes dos produtos
      const productsData = await Promise.all(
        productIds.map(async (productId: string) => {
          const productRef = doc(db, 'products', productId);
          const productSnap = await getDoc(productRef);
          return productSnap.exists() ? { id: productId, ...productSnap.data() } : null;
        })
      );

      // Converter datas para objetos Date
      const startDateTime = new Date(dateRange.startDate);
      startDateTime.setHours(0, 0, 0, 0);
      const endDateTime = new Date(dateRange.endDate);
      endDateTime.setHours(23, 59, 59, 999);

      // Buscar estatísticas para cada produto
      const productStats = await Promise.all(
        productIds.map(async (productId: string) => {
          // Visualizações (total da campanha)
          const viewsRef = collection(db, `campaigns/${campaignId}/views`);
          const viewsQuery = query(viewsRef,
            where('timestamp', '>=', startDateTime.toISOString()),
            where('timestamp', '<=', endDateTime.toISOString())
          );
          const viewsSnap = await getDocs(viewsQuery);

          // Cliques no produto específico
          const clicksRef = collection(db, `campaigns/${campaignId}/clicks`);
          const clicksQuery = query(clicksRef,
            where('productId', '==', productId),
            where('timestamp', '>=', startDateTime.toISOString()),
            where('timestamp', '<=', endDateTime.toISOString())
          );
          const clicksSnap = await getDocs(clicksQuery);

          // Vendas do produto
          const salesRef = collection(db, `campaigns/${campaignId}/sales`);
          const salesQuery = query(salesRef,
            where('productId', '==', productId),
            where('timestamp', '>=', startDateTime.toISOString()),
            where('timestamp', '<=', endDateTime.toISOString())
          );
          const salesSnap = await getDocs(salesQuery);

          // Calcular receita total das vendas
          let revenue = 0;
          salesSnap.forEach(doc => {
            const sale = doc.data();
            revenue += Number(sale.value) || 0;
          });

          const productData = productsData.find(p => p?.id === productId);

          return {
            productId,
            productName: productData?.name || 'Produto não encontrado',
            views: viewsSnap.size,
            clicks: clicksSnap.size,
            sales: salesSnap.size,
            revenue
          };
        })
      );

      setProducts(productStats);
    } catch (err) {
      console.error('Erro ao buscar estatísticas:', err);
      setError('Erro ao carregar estatísticas dos produtos');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-semibold">Estatísticas por Produto</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-6 flex gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Inicial
            </label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              className="border rounded px-3 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Data Final
            </label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              className="border rounded px-3 py-2"
            />
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Produto
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Visualizações
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cliques
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Taxa de Cliques
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vendas
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Taxa de Conversão
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Receita
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {products.map((product) => {
                  const clickRate = product.views > 0 
                    ? ((product.clicks / product.views) * 100).toFixed(2)
                    : '0.00';
                  const conversionRate = product.clicks > 0
                    ? ((product.sales / product.clicks) * 100).toFixed(2)
                    : '0.00';

                  return (
                    <tr key={product.productId}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {product.productName}
                        </div>
                        <div className="text-sm text-gray-500">
                          {product.productId}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {product.views}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {product.clicks}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {clickRate}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {product.sales}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {conversionRate}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Intl.NumberFormat('pt-PT', {
                          style: 'currency',
                          currency: 'EUR'
                        }).format(product.revenue)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
